import { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { CategoryOptions } from '../categories';

const SPENDING_TYPES = [
  { value: 'bills', label: 'Normal Bills' },
  { value: 'controllable', label: 'Controllable' },
  { value: 'unplanned', label: 'Unplanned' },
];

function CategoryConfigPanel({ refreshKey }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category: '', autoSpendingType: '', autoReportable: '', hideSpendingType: false });

  useEffect(() => { fetchConfigs(); }, [refreshKey]);

  async function fetchConfigs() {
    try {
      const res = await apiFetch('/api/category-configs');
      if (res.ok) setConfigs(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    const payload = {
      category: form.category,
      autoSpendingType: form.autoSpendingType || null,
      autoReportable: form.autoReportable === '' ? null : form.autoReportable === 'true',
      hideSpendingType: form.hideSpendingType,
    };
    const res = await apiFetch('/api/category-configs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const saved = await res.json();
      setConfigs((prev) => {
        const idx = prev.findIndex((c) => c.category === saved.category);
        if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
        return [...prev, saved].sort((a, b) => a.category.localeCompare(b.category));
      });
      resetForm();
    }
  }

  async function handleDelete(category) {
    const res = await apiFetch(`/api/category-configs/${encodeURIComponent(category)}`, { method: 'DELETE' });
    if (res.ok) setConfigs((prev) => prev.filter((c) => c.category !== category));
  }

  async function handleSeed() {
    await apiFetch('/api/category-configs/seed', { method: 'POST' });
    fetchConfigs();
  }

  function startEdit(config) {
    setForm({
      category: config.category,
      autoSpendingType: config.autoSpendingType || '',
      autoReportable: config.autoReportable == null ? '' : String(config.autoReportable),
      hideSpendingType: config.hideSpendingType,
    });
    setShowAdd(true);
  }

  function resetForm() {
    setForm({ category: '', autoSpendingType: '', autoReportable: '', hideSpendingType: false });
    setShowAdd(false);
  }

  if (loading) {
    return <section className="card"><h2>Category Configuration</h2><p className="loading">Loading...</p></section>;
  }

  return (
    <section className="card cr-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Category Configuration</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="manage-btn" onClick={handleSeed}>Seed Defaults</button>
          <button className="manage-btn" onClick={() => { resetForm(); setShowAdd(true); }}>+ Add Config</button>
        </div>
      </div>

      <p style={{ color: '#71717a', fontSize: 12, margin: '0 0 12px 0' }}>
        Configure automatic behavior when a category is assigned. Auto-set spending type, reportable status, or hide the spending type selector.
      </p>

      {showAdd && (
        <div className="cr-form" style={{ marginBottom: 12, padding: 12, background: '#16171f', borderRadius: 8, border: '1px solid #2a2b35' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#71717a', fontSize: 13 }}>Category:</span>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              <option value="">Select category...</option>
              {CategoryOptions()}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#71717a', fontSize: 13 }}>Auto spending type:</span>
            <select value={form.autoSpendingType} onChange={(e) => setForm({ ...form, autoSpendingType: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              <option value="">No auto-set</option>
              {SPENDING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span style={{ color: '#71717a', fontSize: 13, marginLeft: 8 }}>Reportable:</span>
            <select value={form.autoReportable} onChange={(e) => setForm({ ...form, autoReportable: e.target.value })}
              className="sfin-tx-select" style={{ width: 'auto' }}>
              <option value="">No override</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, color: '#e4e4e7', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.hideSpendingType}
                onChange={(e) => setForm({ ...form, hideSpendingType: e.target.checked })} />
              Hide spending type
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="manage-btn" onClick={handleSave} disabled={!form.category}>Save</button>
              <button className="manage-btn" onClick={resetForm} style={{ background: '#2a2b35' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="cr-rules-list">
        {configs.length === 0 && <p style={{ color: '#71717a', fontSize: 13 }}>No category configurations. Click "Seed Defaults" to add common ones.</p>}
        {configs.map((config) => (
          <div key={config.category} className="cr-rule-row">
            <div className="cr-rule-info" style={{ flex: 1 }}>
              <span className="sfin-tx-category-badge">{config.category}</span>
              <span className="cr-rule-arrow"> → </span>
              {config.autoSpendingType && (
                <span className={`sfin-tx-spending-badge sfin-st-${config.autoSpendingType}`}>
                  {SPENDING_TYPES.find(t => t.value === config.autoSpendingType)?.label || config.autoSpendingType}
                </span>
              )}
              {config.autoReportable === false && (
                <span style={{ color: '#71717a', fontSize: 11, marginLeft: 4 }}>Not reportable</span>
              )}
              {config.autoReportable === true && (
                <span style={{ color: '#71717a', fontSize: 11, marginLeft: 4 }}>Reportable</span>
              )}
              {config.hideSpendingType && (
                <span style={{ color: '#71717a', fontSize: 11, marginLeft: 4 }}>Hide type</span>
              )}
            </div>
            <div className="cr-rule-actions">
              <button className="wg-action-btn wg-edit-btn" onClick={() => startEdit(config)} title="Edit">&#x270E;</button>
              <button className="wg-action-btn wg-delete-btn" onClick={() => handleDelete(config.category)} title="Delete">&times;</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default CategoryConfigPanel;
